import './shared/resources/fonts/ndd.font';

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CORE_CONFIG_TOKEN } from '@ndk/ng-core/component/core.config';
import { Culture } from '@ndk/ng-i18n';
import { LocalStorageService } from '@ndk/ng-storage';
import { NDDTranslationService } from '@ndk/ng-translation';
import { NDDIntlService } from 'ndd-ng-intl';
import { Subject } from 'rxjs';
import { delay, filter, take, takeUntil } from 'rxjs/operators';

import { AuthTokenService } from '@core/authentication/auth-token.service';
import { RoleTypes } from '@core/authentication/shared/role-types.enum';
import { ICoreConfigInternal } from '@core/core.config.model';
import { LocalStorageKeys } from '@core/storage/local-storage.enum';
import { CustomIntlService } from '@shared/overrides/custom-intl.service';
import { ITranslatedLanguages } from '@shared/regional-settings/regional-settings.model';
import { RegionalSettingsService } from '@shared/regional-settings/regional-settings.service';

@Component({
    selector: 'ndd-app',
    template: require('./app.component.html'),
})
export class AppComponent implements OnInit, OnDestroy {
    private static TRANSLATION_FETCH_DELAY: number = 500;
    private static LANGUAGE_CODE_COUNTRY_SUBSTR: number = 2;

    public showLoader: boolean = true;

    private ngUnsubscribe: Subject<void> = new Subject<void>();
    private window: Window;
    private document: Document;
    private navigator: Navigator;

    private languages: ITranslatedLanguages[];
    private firstLanguageChange: boolean = true;

    constructor(
        @Inject(Window) window: Window,
        @Inject(CORE_CONFIG_TOKEN) private config: ICoreConfigInternal,
        private intlService: NDDIntlService,
        private nddTranslationService: NDDTranslationService,
        private authTokenService: AuthTokenService,
        private localStorageService: LocalStorageService,
        private router: Router,
        private regionalSettings: RegionalSettingsService,
    ) {
        this.window = window;
        this.document = window.document;
        this.navigator = window.navigator;
    }

    public ngOnInit(): void {
        // Inicia acompanhamento com Google Analytics.
        ga('create', this.config.googleAnalyticsTrackingId, 'auto');

        // Configuração para usar o Google Analytics a cada troca de rota.
        this.router.events
            .pipe(
                takeUntil(this.ngUnsubscribe),
                filter((event: any) => event instanceof NavigationEnd),
            )
            .subscribe((event: any) => {
                ga('set', 'page', event.urlAfterRedirects);
                ga('send', 'pageview');

                this.checkTranslations();
            });

        this.changeCSSTheme();

        this.subscribeLanguageChange();
        this.setLanguage();
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();

        this.window.localStorage
            .removeItem(CustomIntlService.LOCAL_STORAGE_CURRENCY_SYMBOL_KEY);
        this.window.localStorage
            .removeItem(CustomIntlService.LOCAL_STORAGE_CURRENCY_CULTURE_KEY);
    }

    private checkTranslations(): void {
        if (!this.firstLanguageChange) {
            const hasTranslations: boolean = !!this.localStorageService.getValue(LocalStorageKeys.Resources);
            if (!hasTranslations) {
                this.setLanguage();
            }
        }
    }

    private setLanguage(): void {
        this.regionalSettings
            .getTranslatedLanguages()
            .pipe(take(1))
            .subscribe((languages: ITranslatedLanguages[]) => {
                this.languages = languages;

                const currentLanguage: string = this.localStorageService.getValue(LocalStorageKeys.CurrentLanguage) || this.navigator.language;
                const culture: Culture = this.handleCurrentTranslationLanguage(currentLanguage);
                this.nddTranslationService.currentLang = culture;
                this.setIntlPartnerCulture(currentLanguage);
            });
    }

    private subscribeLanguageChange(): void {
        // Monitora primeira alteração de idioma.
        this.nddTranslationService.onFirstCurrentLangChanged
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((currentLanguage: Culture) => {
                this.fetchTranslations(currentLanguage);
            });

        // Monitora alterações de idioma.
        this.nddTranslationService.onCurrentLangChanged
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((currentLanguage: Culture) => {
                this.setIntlPartnerCulture(Culture[currentLanguage]);
                this.fetchTranslations(currentLanguage);
            });
    }

    private fetchTranslations(language: Culture): void {
        this.showLoader = true;

        this.nddTranslationService
            .fetch(language.toString())
            .pipe(
                delay(AppComponent.TRANSLATION_FETCH_DELAY),
                take(1),
            )
            .subscribe(() => {
                this.showLoader = false;
                this.firstLanguageChange = false;
                this.document.documentElement.setAttribute('lang', language.toString());
            });
    }
    private setIntlPartnerCulture(culture: string): void {
        // tslint:disable-next-line:no-bitwise
        if (this.authTokenService.role & RoleTypes.Partners) {
            this.intlService.culture = culture;
        }
    }

    private handleCurrentTranslationLanguage(cultureName: string): Culture {
        let language: ITranslatedLanguages = this.languages
            .find((x: ITranslatedLanguages) => x.cultureName.toLowerCase() === cultureName.toLowerCase());

        if (!language) {
            const languageCodeCountry: string = cultureName.substr(0, AppComponent.LANGUAGE_CODE_COUNTRY_SUBSTR);

            language =
                this.languages
                    .find((x: ITranslatedLanguages) => x.cultureName.split('-')[0].toLowerCase().includes(languageCodeCountry.toLowerCase()))
                || this.languages
                    .find((x: ITranslatedLanguages) => x.isDefault);
        }

        let result: string = language.cultureName;
        const split: string[] = result.split('-');
        result = `${ split[0] }-${ split[1].toUpperCase() }`;

        return <unknown>result as Culture;
    }

    private changeCSSTheme(): void {
        const hostName: string = this.window.location.hostname.replace('www.', '');
        if (!hostName.startsWith(DEFAULT_HOST) && this.config.customCssFileByDns) {
            const cssFileName: string = this.cssFileNameByDns(hostName);
            if (!cssFileName) { return; }

            this.appendCustomCSSTheme(cssFileName);
        }
    }

    /**
     * Insere CSS personalizado para sobrescrever CSS padrão.
     * @param name - Nome do arquivo css.
     */
    private appendCustomCSSTheme(name: string): void {
        const href: string = `${ SKINS_PATH + name }/${ name }.css`; '';
        const link: Element = this.document.createElement('link');

        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('href', href);

        this.document.head.appendChild(link);
    }

    /**
     * Busca o nome do arquivo CSS baseado no DNS.
     * @param hostName - Nome do host.
     */
    private cssFileNameByDns(hostName: string): string {
        for (const dns in this.config.customCssFileByDns) {
            const aliases: string[] = this.config.customCssFileByDns[dns];

            if (aliases.some((a: string) => hostName.toLowerCase() === a.toLowerCase())) {
                return dns;
            }
        }

        return null;
    }
}
